import { useState, useEffect } from 'react';
import { useNavigate, Link, useSearchParams } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { ArrowRight } from 'lucide-react';
import { toast } from '@/components/ui/sonner';
import Logo from '@/components/core/Logo';

// Google Icon SVG Component
const GoogleIcon = () => (
  <svg className="w-5 h-5" viewBox="0 0 24 24">
    <path
      fill="#4285F4"
      d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"
    />
    <path
      fill="#34A853"
      d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"
    />
    <path
      fill="#FBBC05"
      d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"
    />
    <path
      fill="#EA4335"
      d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"
    />
  </svg>
);

export default function LoginPage() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { login, token, redirectToGoogleAuth } = useAuth();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const redirect = searchParams.get('redirect') || '/dashboard';

  // If already logged in, redirect to dashboard
  useEffect(() => {
    if (token) {
      navigate(redirect, { replace: true });
    }
  }, [token, redirect, navigate]);

  // Return null while redirecting to prevent flash of login page
  if (token) return null;

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!email || !password) { toast.error('Fill in all fields'); return; }
    setLoading(true);
    try {
      await login(email, password);
      toast.success('Welcome back!');
      // Navigate directly after successful login
      navigate(redirect, { replace: true });
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Invalid credentials');
      setLoading(false);
    }
  };

  /**
   * Handle Google Sign In
   * REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
   */
  const handleGoogleSignIn = () => {
    redirectToGoogleAuth();
  };

  return (
    <div className="min-h-screen bg-background flex flex-col" data-testid="login-page">
      {/* Minimal Navbar */}
      <nav className="border-b border-border px-6 h-14 flex items-center">
        <Logo data-testid="login-logo" />
      </nav>

      <div className="flex-1 flex items-center justify-center px-6 py-12">
        <div className="w-full max-w-sm animate-fade-in-up">
          <div className="mb-8">
            <h1 className="font-heading text-3xl font-[500] tracking-tight mb-2" data-testid="login-title">Welcome back</h1>
            <p className="font-body text-sm text-muted-foreground">Sign in to continue mapping your knowledge.</p>
          </div>

          {/* Google Sign In Button */}
          <Button
            type="button"
            variant="outline"
            onClick={handleGoogleSignIn}
            data-testid="google-signin-btn"
            className="w-full h-11 mb-6 font-body font-medium border-2 border-border hover:bg-muted/50 flex items-center justify-center gap-3"
          >
            <GoogleIcon />
            Continue with Google
          </Button>

          {/* Divider */}
          <div className="relative mb-6">
            <div className="absolute inset-0 flex items-center">
              <span className="w-full border-t border-border" />
            </div>
            <div className="relative flex justify-center text-xs uppercase">
              <span className="bg-background px-2 text-muted-foreground font-mono">or</span>
            </div>
          </div>

          <form onSubmit={handleSubmit} className="space-y-4" data-testid="login-form">
            <div>
              <Label htmlFor="email" className="font-body text-sm font-medium mb-1.5 block">Email</Label>
              <Input
                id="email" type="email" value={email} onChange={e => setEmail(e.target.value)}
                placeholder="you@example.com"
                className="h-11 bg-card border-2 border-border focus:border-cobalt font-body"
                data-testid="login-email-input"
              />
            </div>
            <div>
              <Label htmlFor="password" className="font-body text-sm font-medium mb-1.5 block">Password</Label>
              <Input
                id="password" type="password" value={password} onChange={e => setPassword(e.target.value)}
                placeholder="Your password"
                className="h-11 bg-card border-2 border-border focus:border-cobalt font-body"
                data-testid="login-password-input"
              />
            </div>
            <Button type="submit" disabled={loading} data-testid="login-submit-btn"
              className="w-full h-11 bg-cobalt text-white hover:bg-cobalt/90 font-body font-medium btn-press">
              {loading ? <span className="spinner !border-white/30 !border-t-white" /> : (
                <span className="flex items-center gap-2">Sign in <ArrowRight className="w-4 h-4" strokeWidth={1.5} /></span>
              )}
            </Button>
          </form>

          <p className="mt-6 text-center font-body text-sm text-muted-foreground">
            Don't have an account?{' '}
            <Link to="/register" className="text-cobalt hover:underline font-medium" data-testid="login-register-link">Create one</Link>
          </p>
        </div>
      </div>
    </div>
  );
}
